import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import type { FeedbackAttachmentKind, FeedbackContext } from '@erp/rpc';
import type { FeedbackKind } from '@erp/shared-types';
import { orpc, rpcClient } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';
import { useCanViewFeedbackPage } from '@/hooks/useCanViewFeedbackPage';
import paths from '@/paths';
import { uploadBlobToPresignedUrl } from '@/lib/uploadToPresignedUrl';
import type { SubmitAttachment } from '@/components/feedback-panel/feedbackAttachmentRules';

export interface SubmitFeedbackArgs {
  message: string;
  kind: FeedbackKind;
  pageUrl?: string;
  pageTitle?: string;
  context?: FeedbackContext;
  attachments: SubmitAttachment[];
  /** File on another user's behalf (needs Feedback:Update). */
  reporterUserId?: string;
}

interface ClaimedAttachment {
  fileId: string;
  kind: FeedbackAttachmentKind;
}

/**
 * Presign → PUT every image (in parallel), then submit with the claimed file
 * ids. Nothing is uploaded until the user actually sends, so a cancelled
 * draft leaves no pending files. Every minted id is remembered before its
 * PUT, so a failure anywhere discards all of them best-effort.
 */
async function uploadAndSubmit(args: SubmitFeedbackArgs) {
  const minted: string[] = [];
  try {
    const claimed: ClaimedAttachment[] = await Promise.all(
      args.attachments.map(async (attachment) => {
        const { fileId, uploadUrl } =
          await rpcClient.core.feedback.createUploadUrl({
            fileName: attachment.fileName,
            contentType: attachment.contentType,
          });
        minted.push(fileId);
        await uploadBlobToPresignedUrl(
          uploadUrl,
          attachment.blob,
          attachment.contentType,
        );
        return { fileId, kind: attachment.kind };
      }),
    );
    return await rpcClient.core.feedback.submit({
      message: args.message,
      kind: args.kind,
      pageUrl: args.pageUrl,
      pageTitle: args.pageTitle,
      context: args.context,
      attachments: claimed,
      reporterUserId: args.reporterUserId,
    });
  } catch (error) {
    await Promise.allSettled(
      minted.map((fileId) => rpcClient.core.feedback.discardUpload({ fileId })),
    );
    throw error;
  }
}

export function useSubmitFeedback() {
  const gt = useGT();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const router = useRouter();
  const navigate = useNavigate();
  const canViewFeedbackPage = useCanViewFeedbackPage();

  return useMutation<{ id: string }, Error, SubmitFeedbackArgs>({
    mutationFn: uploadAndSubmit,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.core.feedback.list.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.core.feedback.summary.key(),
      });
      showToast({
        title: gt('Feedback sent', {
          context: 'toast title after feedback was submitted',
        }),
        message: gt('Thanks for helping us improve the product.', {
          context: 'toast body after feedback was submitted',
        }),
        color: 'success',
        icon: 'check',
        link: canViewFeedbackPage
          ? {
              label: gt('View all feedback', {
                context: 'toast link to the feedback page after sending one',
              }),
              href: router.buildLocation({
                to: paths.authenticated.feedbackTriage,
              }).href,
              onClick: () =>
                void navigate({ to: paths.authenticated.feedbackTriage }),
            }
          : undefined,
      });
    },
    onError: (error) => {
      showToast({
        title: gt('Could not send feedback', {
          context: 'toast title when submitting feedback failed',
        }),
        message: error.message,
        color: 'error',
        icon: 'x',
      });
    },
  });
}
